import type { WritableComputedRef } from 'vue';
import type { AnyItem, CompositePrimaryKey, RelationOperationToOne, ResolvedMonospaceSingleRelationField } from '#layers/data-model/public';
import type { FormBlockContext } from '#layers/forms/types/form-block';
import { toValue } from 'vue';
import { randomUuid } from '#layers/base/public';
import { getItemPrimaryKey, getRelationConstraint, getRelationPrimitiveFields, isConnectToOne, isSameKey, nestedToOneConnect } from '#layers/data-model/public';
import {
	createdItemKey,
	fromReferencedItemKey,
	verifyItemKeys,
} from '#layers/forms/composables/relation-field-state-primitives';
import { syncRelationForeignKeys } from './relation-to-one-form-state';

export function createRelationFieldStateToOneInCreateHandler(field: ResolvedMonospaceSingleRelationField, fieldValue: WritableComputedRef<RelationOperationToOne | undefined, RelationOperationToOne | undefined>, formBlockContext: FormBlockContext | null) {
	function handleCreate(payload: AnyItem) {
		fieldValue.value = { _create: { data: { ...payload, [createdItemKey]: randomUuid() } } };
		if (!getRelationConstraint(field).thisSideConstrained) return;

		const { fields: pkFields } = getRelationPrimitiveFields(field);
		syncRelationForeignKeys(formBlockContext, pkFields.map(({ apiName }) => [apiName, null] as const));
	}

	function handleConnect(key: CompositePrimaryKey) {
		fieldValue.value = nestedToOneConnect({ key, mode: 'create' });

		const primitiveValues = fromReferencedItemKey(key, field);
		if (!primitiveValues) return;

		syncRelationForeignKeys(formBlockContext, Object.entries(primitiveValues));
	}

	function handleDisconnect(key: CompositePrimaryKey) {
		fieldValue.value = undefined;

		// make sure the primitive fields on this item are set to `null`
		const primitiveValues = fromReferencedItemKey(key, field);
		if (!primitiveValues) return;

		syncRelationForeignKeys(formBlockContext, Object.keys(primitiveValues).map((fieldKey) => [fieldKey, null] as const));
	}

	function handleUpdate(originalKey: CompositePrimaryKey | null, payload: AnyItem) {
		const key = originalKey ?? getItemPrimaryKey(payload, toValue(field).oppositeCollection!);
		const isNewlyConnectedItem = isConnectToOne(fieldValue.value!) && isSameKey(fieldValue.value!._connect.key, key);
		verifyItemKeys(key, payload, toValue(field).oppositeCollection!);

		if (!isNewlyConnectedItem) {
			fieldValue.value = { _create: { data: { ...payload, [createdItemKey]: randomUuid() } } };
		}
		else {
			throw new Error('Update after connect is currently not supposed.');
		}
	}

	return {
		handleConnect,
		handleDisconnect,
		handleCreate,
		handleUpdate,
	};
}
